<template>
    <div class="mx-auto w-full max-w-[1168px] pb-40 px-8 pt-6">
        <template v-if="state==='loaded'">
            <ui-header :title="`Dayworks Sheet ${dayworksSheet.referenceNumber}`"
                       :subtitle="'ID #'+this.dayworksSheet.id">
                <template #actions>
                    <ui-dropdown placement="bottom-end" menu-class="p-3 w-72" v-if="dayworksSheet.status==='Draft'">
                        <template #default="{ toggle }">
                            <ui-button-new @click="toggle" primary>
                                Actions
                                <i class="flex-shrink-0 ml-2 fa fa-chevron-down"></i>
                            </ui-button-new>
                        </template>

                        <template #menu="{ close }">
                            <ui-dropdown-item @click.prevent="edit();" icon="fas fa-edit">Edit
                            </ui-dropdown-item>
                            <ui-dropdown-divider v-if="dayworksSheet.status==='Draft'"/>
                            <ui-dropdown-item @click.prevent="openDeleteModal();" v-if="dayworksSheet.status==='Draft'"
                                              danger icon="fa fa-trash">Delete
                            </ui-dropdown-item>
                        </template>
                    </ui-dropdown>
                </template>
            </ui-header>

            <!-- General Info -->
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 pb-2">
                <div>
                    <label class="block text-sm font-medium text-gray-700">Date</label>
                    <div
                        class="m-0 px-3 py-2 relative rounded-md shadow-sm block w-full border sm:text-sm focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 border-gray-300 text-gray-500 bg-gray-50 whitespace-pre"
                        v-html="highlight(moment(dayworksSheet.date).format('MMMM Do, YYYY'))">
                    </div>
                </div>
                <div>
                    <label class="block text-sm font-medium text-gray-700">Location of Works</label>
                    <div
                        class="m-0 px-3 py-2 relative rounded-md shadow-sm block w-full border sm:text-sm focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 border-gray-300 bg-gray-50 whitespace-pre"
                        :class="dayworksSheet.locationOfWorks?'text-gray-500':'text-gray-300'"
                        v-html="dayworksSheet.locationOfWorks?highlight(dayworksSheet.locationOfWorks):'no location of works'">
                    </div>
                </div>
                <div>
                    <label class="block text-sm font-medium text-gray-700">Name</label>
                    <div
                        class="m-0 px-3 py-2 relative rounded-md shadow-sm block w-full border sm:text-sm focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 border-gray-300 bg-gray-50 whitespace-pre"
                        :class="dayworksSheet.name?'text-gray-500':'text-gray-300'"
                        v-html="dayworksSheet.name?highlight(dayworksSheet.name):'No name'">
                    </div>
                </div>
            </div>

            <!-- Works Description -->
            <div class="mt-4">
                <div class="col-span-full">
                    <label class="block text-sm font-medium text-gray-700 mb-1"
                           v-text="'Description of Works'"></label>
                    <div
                        class="min-h-[76px] m-0 px-3 py-2 relative rounded-md shadow-sm block w-full border sm:text-sm focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 border-gray-300 bg-gray-50 whitespace-pre"
                        :class="dayworksSheet.descriptionOfWorks?'text-gray-500':'text-gray-300'"
                        v-html="dayworksSheet.descriptionOfWorks?highlight(dayworksSheet.descriptionOfWorks):'No description of works'">
                    </div>
                </div>
            </div>

            <!-- Impact on Programme -->
            <div class="mt-4">
                <label class="block text-sm font-medium text-gray-700 mb-1">Will this impact the programme?</label>
                <div class="flex items-center space-x-6 mt-1">
                    <label class="inline-flex items-center text-sm text-gray-700">
                        <input type="radio" name="impactProgramme" :value="true" disabled
                               v-model="dayworksSheet.willImpactProgramme"
                               class="text-blue-600 border-gray-300 focus:ring-blue-500">
                        <span class="ml-2">Yes</span>
                    </label>
                    <label class="inline-flex items-center text-sm text-gray-700">
                        <input type="radio" name="impactProgramme" :value="false"
                               v-model="dayworksSheet.willImpactProgramme" disabled
                               class="text-blue-600 border-gray-300 focus:ring-blue-500">
                        <span class="ml-2">No</span>
                    </label>
                </div>
            </div>

            <!-- Labour, Plant, Materials Table -->
            <div class="mt-6">
                <h2 class="text-md font-semibold text-gray-700 mb-2">Dayworks Breakdown</h2>
                <div
                    class="flex mt-4 border-t border-b border-gray-200 bg-gray-50 text-xs text-gray-500 font-semibold">
                    <div class="w-6 flex-shrink-0"></div>
                    <div class="border-l border-r border-gray-200 px-2 py-1 w-1/4">Type</div>
                    <div class="border-r border-gray-200 px-2 py-1 flex-grow">Description</div>
                    <div class="-ml-px border-r border-gray-200 px-2 py-1 w-1/4">Qty</div>
                    <div class="w-6 flex-shrink-0 ml-2"></div>
                </div>
                <dayworks-breakdown-item v-for="(item,key) in dayworksSheet.breakdown" :key="item.index"
                                         :item="item"
                                         :readonly="true"
                ></dayworks-breakdown-item>
            </div>
            <div class="mt-6">
                <ui-input-file-inline label="Image, Videos and PDFs"
                                      name="media[]"
                                      :filesystemId="dayworksSheet.filesystemId"
                                      :readonly="true"
                                      :downloadUrl="downloadUrl"
                                      :current-files="getDayworksFiles"></ui-input-file-inline>
            </div>
            <template v-if="dayworksSheet.response">
                <h2 class="text-md font-semibold text-gray-700 mb-2 mt-4">Approval Request</h2>
                <ui-data-grid>
                    <ui-data-grid-item
                        :label="{'Approved':'Approved By','Rejected':'Rejected By'}[dayworksSheet.response.type]"
                        :value="dayworksSheet.response.name"></ui-data-grid-item>
                    <ui-data-grid-item
                        :label="{'Approved':'Approved At','Rejected':'Rejected At'}[dayworksSheet.response.type]"
                        :value="dayworksSheet.response.dateTime"></ui-data-grid-item>
                    <div class="lg:col-span-2" v-if="dayworksSheet.response.type==='Approved'">
                        <div class="text-sm font-medium text-gray-500">Approval Signature</div>
                        <div class="mt-1 text-sm text-gray-900 w-full">
                            <img v-if="dayworksSheet.response.signature" :src="dayworksSheet.response.signature"
                                 style="height:48px">
                        </div>
                    </div>
                    <div class="lg:col-span-2" v-else-if="dayworksSheet.response.type==='Rejected'">
                        <div class="text-sm font-medium text-gray-500">Rejection Comment</div>
                        <div class="mt-1 text-sm text-gray-900 w-full" v-text="dayworksSheet.response.comment"></div>
                    </div>
                </ui-data-grid>
            </template>
            <send-log :send-log="combineSendLogs" @revoke="revoke" @resend="resend" :key="sendLogKey"></send-log>
            <ui-data-grid-item class="mt-8" label="Quick action" wide>
                <div class="flex flex-col md:flex-row flex-wrap -m-2">
                    <ui-button-new class="m-2" icon="fas fa-route" extra
                                   v-if="dayworksSheet.status==='Draft' || dayworksSheet.status==='Sent'"
                                   @click="sendForApproval"
                    >Send for Approval
                    </ui-button-new>
                    <ui-button-new class="m-2" icon="fas fa-route" extra v-else
                                   @click="send"
                    >Send
                    </ui-button-new>
                </div>
            </ui-data-grid-item>

            <ui-data-grid class="mt-6">
                <ui-data-grid-item label="Created By">{{ dayworksSheet.createdBy }} on {{ dayworksSheet.createdOn }}
                </ui-data-grid-item>
                <ui-data-grid-item label="Last Updated By" v-if="dayworksSheet.lastUpdatedBy">{{
                        dayworksSheet.lastUpdatedBy
                    }} on {{
                        dayworksSheet.lastUpdatedOn
                    }}
                </ui-data-grid-item>
            </ui-data-grid>
        </template>
    </div>
</template>

<script>
import UiContainer from "../../ui/UiContainer.vue";
import UiDatePicker from "../../ui/UiDatePicker.vue";
import UiButtonNew from "../../ui/UiButtonNew.vue";
import UiDataGridItem from "../../ui/UiDataGridItem.vue";
import UiHeader from "../../ui/UiHeader.vue";
import UiInput from "../../ui/UiInput.vue";
import UiToggle from "../../ui/UiToggle.vue";
import moment from "moment";
import {DayworksSheet} from "@models";
import UiSelect from "../../ui/UiSelect.vue";
import DayworksBreakdownItem from "./DayworksBreakdownItem.vue";
import draggable from 'vuedraggable'
import UiDropdown from "../../ui/UiDropdown.vue";
import UiDataGrid from "../../ui/UiDataGrid.vue";
import UiDropdownItem from "../../ui/UiDropdownItem.vue";
import SendLog from "../../send-log/SendLog.vue";

export default {
    components: {
        SendLog,
        UiDropdownItem, UiDataGrid, UiDropdown,
        draggable,
        DayworksBreakdownItem,
        UiSelect,
        UiToggle, UiInput, UiHeader, UiDataGridItem, UiDatePicker, UiButtonNew, UiContainer
    },
    props: {
        item: Object,
        packageReference: null,
        setEditMode: Function,
        removeItem: Function,
        search: '',
    },
    data() {
        return {
            dayworksSheet: {},
            busy: false,
            dateTimeError: '',
            moment,
            state: 'loading',
            sendLogKey: 0,
        }
    },
    computed: {
        combineSendLogs() {
            return this.dayworksSheet.sendLog.map((a) => {
                return a;
            }).sort((a, b) => moment(b.requested.at).unix() - moment(a.requested.at).unix())
        }
    },
    methods: {
        highlight(string) {
            string = '' + string;
            if (!string || !(typeof string === 'string' || string instanceof String)) {
                return '&nbsp;'
            }
            let htmlSafe = string.replace(/[\u00A0-\u9999<>\&;0123456789#]/g, i => '&#' + i.charCodeAt(0) + ';')
            if (this.search.trim() === '') {
                return htmlSafe
            }
            let search = this.search.toLowerCase().replace(/[\u00A0-\u9999<>\&;0123456789#]/g, i => '&#' + i.charCodeAt(0) + ';')
            const query = search.split(/[\s]+/).filter((item) => {
                return item.trim() !== ''
            })
            let reg = new RegExp(query.join('|'), 'gi');
            let res = htmlSafe.replace(reg, "<span class='bg-blue-200'>$&</span>");
            if (res === '<span class=\'bg-blue-200\'></span>') {
                return '&nbsp;'
            }

            return res
        },
        downloadUrl(id) {
            return route('api.v2.project-delivery.dayworks-sheet.file.download', {
                'package_reference': this.packageReference,
                'id': this.item.id,
                'file_id': id
            })
        },
        async getDayworksFiles() {
            let data = await DayworksSheet.find(this.packageReference, this.item.id)
            return data.files
        },
        edit() {
            this.setEditMode(true);
        },
        send() {
            if (this.state === 'loaded') {
                this.$modals.open('dayworks-sheet-send', {
                    title: `Send Dayworks Sheet`,
                    button: 'Send',
                    dayworksSheet: this.dayworksSheet,
                    handle: async (messageId, modal) => {
                        modal.close()
                    }
                })
            } else {
                setTimeout(() => this.send(), 100)
            }
        },
        revoke(i) {
            this.dayworksSheet.revoke(i.id);
        },
        resend(i) {
            this.dayworksSheet.resend(i.id);
        },
        sendForApproval() {
            if (this.state === 'loaded') {
                this.$modals.open('dayworks-sheet-send-for-approval', {
                    title: `Send Dayworks Sheet for Approval`,
                    button: 'Send for Approval',
                    dayworksSheet: this.dayworksSheet,
                    handle: async (data, modal) => {
                        modal.close()
                        this.sendLogKey++;
                    }
                })
            } else {
                setTimeout(() => this.sendForApproval(), 100)
            }
        },
        openDeleteModal() {
            const title = 'Are you sure you want to remove Dayworks Sheet ' + this.item.referenceNumber + '?'
            const description = 'You are removing Dayworks Sheet #' + this.item.id + '. Are you sure you want to do this?'
            eventBus.$emit('delete-modal-open', title, description, async () => {
                await this.dayworksSheet.delete()
                this.removeItem(this.item)
            });
        },
        print() {

        },
        async updateFromListener(data) {
            if (data.operation === 'updateAggregate') {
                if ((data.aggregate.type === 'DayworksSheet') && (data.aggregate.id === this.dayworksSheet.id)) {
                    Object.assign(this.dayworksSheet, data.aggregate.data)
                    Object.assign(this.item, data.aggregate.data)
                    this.sendLogKey++;
                }
            }
        },
    },
    async mounted() {
        this.dayworksSheet = await DayworksSheet.find(this.packageReference, this.item.id);
        this.dayworksSheet.breakdown.forEach((i) => {
            i.quantity = parseFloat(i.quantity) ? i.quantity : ''
        });
        if (this.dayworksSheet) {
            User.registerAggregate({'type': 'DayworksSheet', 'id': this.dayworksSheet.id}, this.updateFromListener)
        }
        this.state = 'loaded'
    },
}
</script>
